"use client";

import * as React from "react";

import {
  getAdminNavigationPathname,
  type AdminNavigationDestination,
} from "@/lib/adminNavigation";
import { usePathname } from "@/lib/router-compat";

const FAVORITE_LIMIT = 5;
const RECENT_LIMIT = 5;

function storageKey(kind: "favorites" | "recent", userKey: string): string {
  return `sumacomi:admin-navigation:${kind}:v1:${userKey || "anonymous"}`;
}

function readStoredIds(key: string): string[] {
  if (typeof window === "undefined") return [];

  try {
    const value: unknown = JSON.parse(window.localStorage.getItem(key) ?? "[]");
    if (!Array.isArray(value)) return [];
    return value.filter((id): id is string => typeof id === "string");
  } catch {
    return [];
  }
}

function writeStoredIds(key: string, ids: readonly string[]) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(key, JSON.stringify(ids));
  } catch {
    // Storageが利用できない場合も、現在のセッション中はstateで維持する。
  }
}

function findCurrentDestination(
  destinations: readonly AdminNavigationDestination[],
  pathname: string
): AdminNavigationDestination | undefined {
  return destinations
    .filter((destination) => {
      const destinationPathname = getAdminNavigationPathname(destination.url);
      return (
        pathname === destinationPathname ||
        pathname.startsWith(`${destinationPathname}/`)
      );
    })
    .sort((a, b) => b.url.length - a.url.length)[0];
}

function resolveDestinations(
  ids: readonly string[],
  destinations: readonly AdminNavigationDestination[]
): AdminNavigationDestination[] {
  const byId = new Map(
    destinations.map((destination) => [destination.id, destination])
  );
  return ids
    .map((id) => byId.get(id))
    .filter(
      (destination): destination is AdminNavigationDestination =>
        destination !== undefined
    );
}

export function useNavigationPreferences({
  userKey,
  destinations,
}: {
  userKey: string;
  destinations: readonly AdminNavigationDestination[];
}) {
  const pathname = usePathname();
  const favoriteStorageKey = storageKey("favorites", userKey);
  const recentStorageKey = storageKey("recent", userKey);
  const [favoriteIds, setFavoriteIds] = React.useState<string[]>(() =>
    readStoredIds(favoriteStorageKey).slice(0, FAVORITE_LIMIT)
  );
  const [recentIds, setRecentIds] = React.useState<string[]>(() =>
    readStoredIds(recentStorageKey).slice(0, RECENT_LIMIT)
  );

  React.useEffect(() => {
    setFavoriteIds(readStoredIds(favoriteStorageKey).slice(0, FAVORITE_LIMIT));
    setRecentIds(readStoredIds(recentStorageKey).slice(0, RECENT_LIMIT));
  }, [favoriteStorageKey, recentStorageKey]);

  React.useEffect(() => {
    const current = findCurrentDestination(destinations, pathname);
    if (!current) return;

    setRecentIds((ids) => {
      const next = [current.id, ...ids.filter((id) => id !== current.id)].slice(
        0,
        RECENT_LIMIT
      );
      writeStoredIds(recentStorageKey, next);
      return next;
    });
  }, [destinations, pathname, recentStorageKey]);

  const toggleFavorite = React.useCallback(
    (id: string) => {
      setFavoriteIds((ids) => {
        const next = ids.includes(id)
          ? ids.filter((favoriteId) => favoriteId !== id)
          : ids.length < FAVORITE_LIMIT
            ? [...ids, id]
            : ids;
        writeStoredIds(favoriteStorageKey, next);
        return next;
      });
    },
    [favoriteStorageKey]
  );

  const favoriteDestinations = React.useMemo(
    () => resolveDestinations(favoriteIds, destinations),
    [destinations, favoriteIds]
  );
  const recentDestinations = React.useMemo(
    () =>
      resolveDestinations(recentIds, destinations).filter(
        (destination) => !favoriteIds.includes(destination.id)
      ),
    [destinations, favoriteIds, recentIds]
  );

  return {
    favoriteIds,
    favoriteDestinations,
    recentDestinations,
    favoriteLimit: FAVORITE_LIMIT,
    toggleFavorite,
  };
}
